import { ActivityStatus, Priority, ProjectHealth, RiskSeverity } from '@kainos/shared';

/**
 * The five visual tones of the design, and the mapping from each domain enum
 * onto one of them. Colours are the ones used in the approved screens.
 */
export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface ToneStyle {
  /** Background of pills and soft panels. */
  bg: string;
  /** Text and icon colour. */
  fg: string;
  /** Border for outlined variants. */
  border: string;
  /** Solid fill for dots, bars and accents. */
  solid: string;
}

export const TONE_STYLES: Record<Tone, ToneStyle> = {
  neutral: {
    bg: 'bg-neutral-soft',
    fg: 'text-ink-muted',
    border: 'border-line',
    solid: 'bg-ink-muted',
  },
  info: {
    bg: 'bg-primary-soft',
    fg: 'text-primary',
    border: 'border-primary-border',
    solid: 'bg-primary',
  },
  success: {
    bg: 'bg-success-soft',
    fg: 'text-success',
    border: 'border-success-border',
    solid: 'bg-success',
  },
  warning: {
    bg: 'bg-warning-soft',
    fg: 'text-warning',
    border: 'border-warning-border',
    solid: 'bg-warning',
  },
  danger: {
    bg: 'bg-danger-soft',
    fg: 'text-danger',
    border: 'border-danger-border',
    solid: 'bg-danger',
  },
};

/** Raw hex values, for the few places that need inline colour (SVG, charts). */
export const TONE_HEX: Record<Tone, string> = {
  neutral: '#64748B',
  info: '#2563EB',
  success: '#16A34A',
  warning: '#F97316',
  danger: '#DC2626',
};

export const ACTIVITY_STATUS_TONE: Record<ActivityStatus, Tone> = {
  [ActivityStatus.PENDING]: 'neutral',
  [ActivityStatus.IN_PROGRESS]: 'info',
  [ActivityStatus.BLOCKED]: 'danger',
  [ActivityStatus.IN_REVIEW]: 'warning',
  [ActivityStatus.DONE]: 'success',
  [ActivityStatus.CANCELLED]: 'neutral',
};

export const PROJECT_HEALTH_TONE: Record<ProjectHealth, Tone> = {
  [ProjectHealth.NORMAL]: 'success',
  [ProjectHealth.ATTENTION]: 'warning',
  [ProjectHealth.CRITICAL]: 'danger',
};

export const PRIORITY_TONE: Record<Priority, Tone> = {
  [Priority.LOW]: 'neutral',
  [Priority.MEDIUM]: 'info',
  [Priority.HIGH]: 'warning',
  [Priority.CRITICAL]: 'danger',
};

export const RISK_SEVERITY_TONE: Record<RiskSeverity, Tone> = {
  [RiskSeverity.LOW]: 'neutral',
  [RiskSeverity.MEDIUM]: 'info',
  [RiskSeverity.HIGH]: 'warning',
  [RiskSeverity.CRITICAL]: 'danger',
};
